import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Lightbulb, Globe, Check } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'overview', title: 'Language Support Overview' },
  { id: 'supported-languages', title: 'Supported Languages' },
  { id: 'how-it-works', title: 'How It Works' },
  { id: 'ui-language', title: 'UI Interface Language' },
  { id: 'tips', title: 'Tips for Best Results' },
  { id: 'faq', title: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'How to navigate the SweetPilot web app', href: '/doc/article/navigate-web-app' },
  { title: 'SweetPilot AI Employees', href: '/employees' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
];

const supportedLanguages = [
  { name: 'English', native: 'English', flag: '🇺🇸' },
  { name: 'Spanish', native: 'Español', flag: '🇪🇸' },
  { name: 'French', native: 'Français', flag: '🇫🇷' },
  { name: 'German', native: 'Deutsch', flag: '🇩🇪' },
  { name: 'Portuguese', native: 'Português', flag: '🇧🇷' },
  { name: 'Italian', native: 'Italiano', flag: '🇮🇹' },
  { name: 'Dutch', native: 'Nederlands', flag: '🇳🇱' },
  { name: 'Russian', native: 'Русский', flag: '🇷🇺' },
  { name: 'Chinese (Simplified)', native: '简体中文', flag: '🇨🇳' },
  { name: 'Chinese (Traditional)', native: '繁體中文', flag: '🇹🇼' },
  { name: 'Japanese', native: '日本語', flag: '🇯🇵' },
  { name: 'Korean', native: '한국어', flag: '🇰🇷' },
  { name: 'Arabic', native: 'العربية', flag: '🇸🇦' },
  { name: 'Hindi', native: 'हिन्दी', flag: '🇮🇳' },
  { name: 'Bengali', native: 'বাংলা', flag: '🇧🇩' },
  { name: 'Turkish', native: 'Türkçe', flag: '🇹🇷' },
  { name: 'Vietnamese', native: 'Tiếng Việt', flag: '🇻🇳' },
  { name: 'Thai', native: 'ไทย', flag: '🇹🇭' },
  { name: 'Indonesian', native: 'Bahasa Indonesia', flag: '🇮🇩' },
  { name: 'Polish', native: 'Polski', flag: '🇵🇱' },
  { name: 'Ukrainian', native: 'Українська', flag: '🇺🇦' },
  { name: 'Greek', native: 'Ελληνικά', flag: '🇬🇷' },
  { name: 'Hebrew', native: 'עברית', flag: '🇮🇱' },
  { name: 'Swedish', native: 'Svenska', flag: '🇸🇪' },
  { name: 'Norwegian', native: 'Norsk', flag: '🇳🇴' },
  { name: 'Danish', native: 'Dansk', flag: '🇩🇰' },
  { name: 'Finnish', native: 'Suomi', flag: '🇫🇮' },
  { name: 'Czech', native: 'Čeština', flag: '🇨🇿' },
  { name: 'Romanian', native: 'Română', flag: '🇷🇴' },
  { name: 'Hungarian', native: 'Magyar', flag: '🇭🇺' },
];

interface TipBoxProps {
  children: React.ReactNode;
  isDark: boolean;
}

function TipBox({ children, isDark }: TipBoxProps) {
  return (
    <div className={`flex gap-3 p-4 rounded-xl my-6 ${
      isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'
    }`}>
      <Lightbulb className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
      <div className={`text-sm ${isDark ? 'text-purple-200' : 'text-purple-800'}`}>
        {children}
      </div>
    </div>
  );
}

export default function UILanguages() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'What Languages is the SweetPilot UI Available In? - Multilingual Support',
    description: 'Learn about SweetPilot multilingual capabilities. AI employees support 30+ languages with automatic detection. Discover UI language options and tips for best results.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/ui-languages' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>What Languages is the SweetPilot UI Available In? - Multilingual Support | SweetPilot Help Center</title>
        <meta name="description" content="Discover SweetPilot's multilingual capabilities. AI employees support 30+ languages with automatic detection. Learn about UI language options and tips for optimal results." />
        <meta name="keywords" content="SweetPilot languages, multilingual AI, AI language support, SweetPilot translation, international AI assistant, multi-language chatbot, AI localization" />
        <meta property="og:title" content="SweetPilot UI Language Support - 30+ Languages" />
        <meta property="og:description" content="Learn about SweetPilot's multilingual support for AI employees and UI interface options." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/ui-languages" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Language Support" />
        <meta name="twitter:description" content="AI employees supporting 30+ languages with automatic detection." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/ui-languages" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
          {/* Main Content */}
          <div className="flex-1 max-w-2xl">
            {/* Breadcrumbs */}
            <nav className="mb-8">
            <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              <li>
                <Link
                  to="/docs"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  All Collections
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li>
                <Link
                  to="/doc/collections/getting-started"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  Getting started
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li className={isDark ? 'text-white' : 'text-slate-900'}>
                What languages is the UI available in?
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-purple-500/20' : 'bg-purple-100'
              }`}>
                <Globe className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <h1 className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                What languages is the UI available in?
              </h1>
            </div>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Learn about the multilingual capabilities of SweetPilot AI Employees and the interface language options.
            </p>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            {/* Overview */}
            <h2 id="overview" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Language Support Overview
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot AI Employees are designed to communicate fluently in <strong>over 30 languages</strong>. Our AI employees automatically detect the language you're using and respond in that same language, providing a seamless multilingual experience.
            </p>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              This means you can interact with any AI employee in your preferred language without changing any settings. Simply start typing in your language, and the AI will respond naturally in that language.
            </p>

            {/* Supported Languages */}
            <h2 id="supported-languages" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Supported Languages
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Our AI employees can communicate fluently in the following languages:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
              {supportedLanguages.map((lang) => (
                <div
                  key={lang.name}
                  className={`flex items-center gap-2 p-3 rounded-lg border ${
                    isDark
                      ? 'bg-dark-800/50 border-white/10'
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {lang.name}
                    </p>
                    <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      {lang.native}
                    </p>
                  </div>
                  <Check className={`w-4 h-4 shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                </div>
              ))}
            </div>

            <TipBox isDark={isDark}>
              <strong>Note:</strong> This is not an exhaustive list. Our AI employees can communicate in many additional languages and dialects. If your language isn't listed, try using it - there's a good chance it's supported!
            </TipBox>

            {/* How It Works */}
            <h2 id="how-it-works" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              How It Works
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot's multilingual support works automatically:
            </p>
            <ol className={`list-decimal pl-6 mb-6 space-y-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>
                <strong>Automatic Detection:</strong> When you send a message to any AI employee, our system automatically detects the language you're using.
              </li>
              <li>
                <strong>Native Response:</strong> The AI employee responds entirely in your detected language, maintaining natural and fluent communication.
              </li>
              <li>
                <strong>Consistent Experience:</strong> Throughout your conversation, the AI maintains the same language unless you switch languages.
              </li>
              <li>
                <strong>Language Switching:</strong> You can switch languages mid-conversation by simply typing in a different language. The AI will adapt accordingly.
              </li>
            </ol>

            <div className={`p-6 rounded-xl border mb-8 ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-slate-50 border-slate-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Example Conversation:
              </h3>
              <div className="space-y-4">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <p className={`text-sm font-medium mb-1 ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>You (Spanish):</p>
                  <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>"Hola, necesito ayuda con mi estrategia de marketing"</p>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                  <p className={`text-sm font-medium mb-1 ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>AI Employee (Spanish):</p>
                  <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>"¡Hola! Con gusto te ayudo con tu estrategia de marketing. ¿Podrías contarme más sobre tu negocio y tus objetivos?"</p>
                </div>
              </div>
            </div>

            {/* UI Interface Language */}
            <h2 id="ui-language" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              UI Interface Language
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Currently, the SweetPilot web application interface (buttons, menus, navigation) is available in <strong>English</strong>. However, this doesn't affect your ability to communicate with AI employees in any supported language.
            </p>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              We're actively working on translating the user interface into more languages. Stay tuned for updates!
            </p>

            <div className={`p-4 rounded-xl border mb-8 ${
              isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-100'
            }`}>
              <p className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                <strong>Coming Soon:</strong> UI translations for Spanish, French, German, Portuguese, and more languages are planned for future updates.
              </p>
            </div>

            {/* Tips for Best Results */}
            <h2 id="tips" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Tips for Best Results
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To get the best multilingual experience with SweetPilot:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Be consistent:</strong> Try to stick to one language within a conversation for the best results.</li>
              <li><strong>Use proper spelling:</strong> Correct spelling helps the AI better understand and respond in your language.</li>
              <li><strong>Avoid mixing languages:</strong> While the AI can handle it, mixing multiple languages in one message may produce mixed results.</li>
              <li><strong>Specify if needed:</strong> If the AI responds in the wrong language, simply ask it to respond in your preferred language.</li>
              <li><strong>Technical terms:</strong> For technical content, the AI may keep some terms in English. You can ask it to translate these if needed.</li>
            </ul>

            <TipBox isDark={isDark}>
              <strong>Pro Tip:</strong> If you're working with a team that speaks different languages, each team member can interact with the same AI employee in their own language. The AI adapts to each user individually!
            </TipBox>

            {/* FAQ */}
            <h2 id="faq" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Frequently Asked Questions
            </h2>

            <div className="space-y-6 mb-8">
              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Do I need to change any settings to use a different language?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  No! Simply start typing in your preferred language, and the AI will automatically detect and respond in that language.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Can different team members use different languages?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Yes! Each user can communicate in their own language. The AI adapts to each conversation individually.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  What if my language isn't listed?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Try using it anyway! Our AI employees support many languages beyond the main list. If you experience issues, please{' '}
                  <Link
                    to="/contact"
                    className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    contact us
                  </Link>{' '}
                  and let us know.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Are all AI employees equally fluent in all languages?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Yes, all SweetPilot AI employees share the same multilingual capabilities. Whether you're using Nova for customer support or Echo for copywriting, you'll get the same language quality.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Can I get AI-generated content in multiple languages?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Absolutely! You can ask an AI employee to generate content in any supported language, or even translate content from one language to another.
                </p>
              </div>
            </div>

            {/* Divider */}
            <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {article.title}
                  </span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>

            {/* Feedback Section */}
            <div className={`mt-12 p-8 rounded-2xl text-center ${
              isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden lg:block w-56 shrink-0 ml-auto">
          <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
            {/* Sidebar Title */}
            <h3 className={`text-base mb-4 ${
              isAtTop
                ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                : isDark ? 'text-gray-400' : 'text-slate-600'
            }`}>
              UI Languages
            </h3>
            <nav className="space-y-2">
              {tableOfContents.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-1.5 text-sm transition-colors ${
                    activeSection === item.id
                      ? isDark
                        ? 'text-white font-medium'
                        : 'text-slate-900 font-medium'
                      : isAtTop && index === 0
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isDark
                          ? 'text-gray-500 hover:text-gray-300'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🌍</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
      </StaticPageLayout>
    </>
  );
}
